import React, { useState, useEffect } from 'react';
import api from '@/api';
import {
  Key, Calendar, Cpu, Plug, TrendingUp, Clock, Loader2, Euro, BarChart2,
  Download, AlertTriangle, Bell, CheckCircle2, ArrowRight, UserCheck,
  LogOut, Users, Activity, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';

// --- Sub-components ---

function StatCard({ icon: Icon, label, value, sub, color = '#c9a84c', onClick }) {
  return (
    <div
      onClick={onClick}
      className={`card-dark rounded-xl p-5 space-y-2 ${onClick ? 'cursor-pointer hover:border-[var(--lux-border-3)] transition-colors' : ''}`}
    >
      <div className="flex items-center justify-between">
        <p className="text-[var(--lux-text-muted-3)] text-xs uppercase tracking-widest">{label}</p>
        <Icon size={16} strokeWidth={1.5} style={{ color }} />
      </div>
      <p className="font-serif text-3xl text-white font-light">{value}</p>
      {sub && <p className="text-[var(--lux-border-4)] text-xs">{sub}</p>}
    </div>
  );
}

function RevenueBar({ months }) {
  if (!months?.length) return null;
  return (
    <div className="card-dark rounded-xl p-5">
      <div className="flex items-center gap-2 mb-4">
        <BarChart2 size={14} className="text-[#c9a84c]" strokeWidth={1.5} />
        <p className="text-[#c9a84c] text-xs uppercase tracking-widest">Revenue ultimi 6 mesi</p>
      </div>
      <div className="flex items-end gap-2 h-28">
        {months.map((m, i) => (
          <div key={i} className="flex-1 flex flex-col items-center gap-1">
            <p className="text-[var(--lux-border-4)] text-[9px]">€{m.revenue > 999 ? `${(m.revenue / 1000).toFixed(1)}k` : m.revenue}</p>
            <div className="w-full rounded-t-sm bg-[var(--lux-surface-3)] relative overflow-hidden"
              style={{ height: '80px' }}>
              <div
                className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#c9a84c] to-[#c9a84c]/60 transition-all"
                style={{ height: `${m.bar_pct || 0}%` }}
              />
            </div>
            <p className="text-[var(--lux-text-muted-3)] text-[9px] uppercase">{m.month}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

const fmtEur = (v) => `€${Number(v || 0).toLocaleString('it-IT', { minimumFractionDigits: 0 })}`;
const fmtDate = (s) => s ? new Date(s).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' }) : '—';
const fmtTime = (s) => s ? new Date(s).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }) : '';

// Urgency alert types
const URGENCY_CONFIG = {
  supplier: { color: '#ef4444', bg: 'bg-red-950/40', border: 'border-red-800/30', icon: Bell, label: 'Preventivo Fornitore Pronto' },
  checkin: { color: '#f59e0b', bg: 'bg-amber-950/40', border: 'border-amber-800/30', icon: UserCheck, label: 'Check-in Imminente' },
  checkout: { color: '#60a5fa', bg: 'bg-blue-950/40', border: 'border-blue-800/30', icon: LogOut, label: 'Check-out Oggi' },
  quote: { color: '#a78bfa', bg: 'bg-violet-950/40', border: 'border-violet-800/30', icon: Clock, label: 'Preventivo Senza Risposta' },
  pending: { color: '#94a3b8', bg: 'bg-slate-950/40', border: 'border-slate-800/30', icon: AlertTriangle, label: 'In Attesa > 24h' },
};

function UrgencyItem({ type, title, sub, action, onAction }) {
  const cfg = URGENCY_CONFIG[type] || URGENCY_CONFIG.pending;
  const Icon = cfg.icon;
  return (
    <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${cfg.bg} ${cfg.border}`}>
      <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
        style={{ backgroundColor: `${cfg.color}18` }}>
        <Icon size={14} strokeWidth={1.5} style={{ color: cfg.color }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm truncate">{title}</p>
        <p className="text-[var(--lux-text-muted-3)] text-xs">{sub}</p>
      </div>
      {onAction && (
        <button onClick={onAction}
          className="text-[10px] uppercase tracking-widest shrink-0 flex items-center gap-1 px-2 py-1 rounded border border-[var(--lux-border-3)] text-[var(--lux-text-muted)] hover:text-white hover:border-[var(--lux-text-muted-3)] transition-colors">
          {action} <ArrowRight size={10} />
        </button>
      )}
    </div>
  );
}

// --- Main Dashboard ---
export default function Dashboard({ onNavigate }) {
  const [stats, setStats] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [urgency, setUrgency] = useState(null);
  const [loading, setLoading] = useState(true);
  const [urgencyLoading, setUrgencyLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const loadStats = async () => {
    setLoading(true);
    try {
      const [statsRes, revRes] = await Promise.all([
        api.get('/stats/dashboard'),
        api.get('/stats'),
      ]);
      setStats(statsRes.data);
      setRevenue(revRes.data);
    } catch { /* ignore */ } finally {
      setLoading(false);
    }
  };

  const loadUrgency = async () => {
    setUrgencyLoading(true);
    try {
      const res = await api.get('/stats/urgency');
      setUrgency(res.data);
    } catch { /* ignore */ } finally {
      setUrgencyLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
    loadUrgency();
  }, []);

  const handleRefresh = () => {
    loadStats();
    loadUrgency();
    setLastRefresh(new Date());
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const res = await api.get('/stats/export-csv', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `prenotazioni_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Errore esportazione CSV');
    } finally {
      setExporting(false);
    }
  };

  // Count total urgency items
  const urgencyCount = urgency
    ? (urgency.totals?.supplier_ready || 0) +
      (urgency.totals?.checkins_48h || 0) +
      (urgency.totals?.checkouts_today || 0) +
      (urgency.totals?.unconfirmed || 0) +
      (urgency.totals?.long_pending || 0)
    : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="label-gold">Pannello Admin</p>
          <h2 className="font-serif text-3xl text-white font-light mt-1">Dashboard</h2>
        </div>
        <div className="flex items-center gap-2">
          <p className="text-[var(--lux-border-4)] text-[10px]">Aggiornato {lastRefresh.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}</p>
          <button onClick={handleRefresh}
            className="text-[var(--lux-text-muted-3)] hover:text-white transition-colors p-1.5 rounded hover:bg-[var(--lux-surface-3)]"
            data-testid="refresh-dashboard-btn">
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      {/* ===== URGENCY SECTION ===== */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <p className="text-[#c9a84c] text-xs uppercase tracking-widest">Da Gestire</p>
            {urgencyCount > 0 && (
              <span className="min-w-[20px] h-5 px-1.5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                {urgencyCount}
              </span>
            )}
          </div>
        </div>

        {urgencyLoading ? (
          <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin text-[#c9a84c]" /></div>
        ) : urgencyCount === 0 ? (
          <div className="card-dark rounded-xl px-5 py-6 flex items-center gap-3">
            <CheckCircle2 size={18} className="text-green-400 shrink-0" strokeWidth={1.5} />
            <p className="text-[var(--lux-text-muted)] text-sm">Tutto sotto controllo — nessuna azione urgente richiesta.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {/* Supplier quotes ready */}
            {urgency?.supplier_quotes_ready?.map((r, i) => (
              <UrgencyItem key={`sup-${i}`} type="supplier"
                title={`${r.guest_name} — preventivo fornitore pronto`}
                sub={`${r.checkin_date} → ${r.checkout_date}`}
                action="Processa"
                onAction={() => onNavigate?.('booking-requests')}
              />
            ))}

            {/* Checkins in 48h */}
            {urgency?.checkins_48h?.map((b, i) => (
              <UrgencyItem key={`ci-${i}`} type="checkin"
                title={`${b.guest_name} — check-in imminente`}
                sub={`${fmtDate(b.checkin_at)} · ${b.property_name || ''}`}
                action="Chiavi"
                onAction={() => onNavigate?.('bookings')}
              />
            ))}

            {/* Checkouts today */}
            {urgency?.checkouts_today?.map((b, i) => (
              <UrgencyItem key={`co-${i}`} type="checkout"
                title={`${b.guest_name} — check-out ${fmtDate(b.checkout_at)}`}
                sub={`Orario previsto: ${fmtTime(b.checkout_at) || '11:00'}`}
                action="Chiavi"
                onAction={() => onNavigate?.('bookings')}
              />
            ))}

            {/* Unconfirmed quotes > 24h */}
            {urgency?.unconfirmed_quotes?.map((r, i) => (
              <UrgencyItem key={`uq-${i}`} type="quote"
                title={`${r.guest_name} — preventivo senza risposta da ${r.hours_waiting}h`}
                sub={`Check-in: ${r.checkin_date} · Totale: ${fmtEur(r.total_price)}`}
                action="Richieste"
                onAction={() => onNavigate?.('booking-requests')}
              />
            ))}

            {/* Long pending > 24h */}
            {urgency?.long_pending?.map((r, i) => (
              <UrgencyItem key={`lp-${i}`} type="pending"
                title={`${r.guest_name} — in attesa da ${r.hours_pending}h`}
                sub={`Check-in: ${r.checkin_date}`}
                action="Richieste"
                onAction={() => onNavigate?.('booking-requests')}
              />
            ))}
          </div>
        )}
      </div>

      {/* ===== KPI CARDS ===== */}
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 size={24} className="animate-spin text-[#c9a84c]" /></div>
      ) : (
        <>
          {/* Quick stats row */}
          {stats && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatCard icon={Activity} label="Chiavi Attive" value={urgency?.active_keys_count ?? stats?.active_keys ?? 0}
                sub="Ospiti in struttura ora" color="#22c55e"
                onClick={() => onNavigate?.('bookings')} />
              <StatCard icon={Users} label="In Attesa" value={revenue?.status_counts?.pending || 0}
                sub="Richieste da processare" color="#f59e0b"
                onClick={() => onNavigate?.('booking-requests')} />
              <StatCard icon={Calendar} label="Preventivate" value={revenue?.status_counts?.quoted || 0}
                sub="In attesa di conferma ospite" color="#60a5fa"
                onClick={() => onNavigate?.('booking-requests')} />
              <StatCard icon={CheckCircle2} label="Confermate" value={revenue?.confirmed_count || 0}
                sub="Prenotazioni totali" color="#a78bfa"
                onClick={() => onNavigate?.('booking-requests')} />
            </div>
          )}

          {/* Revenue stats */}
          {revenue && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <StatCard icon={Euro} label="Revenue Totale" value={fmtEur(revenue.total_revenue)}
                sub="Sole prenotazioni confermate" color="#c9a84c" />
              <StatCard icon={TrendingUp} label="Valore Medio" value={fmtEur(revenue.avg_booking_value)}
                sub="Per prenotazione" color="#f59e0b" />
              <StatCard icon={BarChart2} label="Richieste Totali" value={revenue.total_requests}
                sub="Tutti gli stati" color="#60a5fa" />
            </div>
          )}

          {/* System stats */}
          {stats && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatCard icon={Key} label="Chiavi Totali" value={stats.total_keys || 0}
                sub="Incluse scadute" color="#888" />
              <StatCard icon={Cpu} label="Dispositivi" value={stats.total_devices || 0}
                sub="Configurati" color="#888" />
              <StatCard icon={Plug} label="Integrazioni" value={stats.active_integrations || 0}
                sub="Smart home attive" color="#888" />
              <StatCard icon={Clock} label="Prossimi check-in" value={urgency?.totals?.checkins_48h || 0}
                sub="Nelle prossime 48h" color="#f59e0b" />
            </div>
          )}

          {/* Revenue bar chart + CSV export */}
          {revenue?.recent_6_months && (
            <div className="space-y-2">
              <RevenueBar months={revenue.recent_6_months} />
              <div className="flex justify-end">
                <button
                  onClick={handleExportCSV}
                  disabled={exporting}
                  className="btn-ghost flex items-center gap-2 text-xs border border-[var(--lux-border-3)] hover:border-[var(--lux-text-muted-3)] px-4 py-2"
                  data-testid="export-csv-btn"
                >
                  {exporting ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                  Esporta CSV prenotazioni
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
